import jwtDecode from 'jwt-decode'
import { z } from 'zod'
import { Account } from '../data/model/accountUser'
import { GetAccountsByEmail } from '../data/rest/accountUser'
import { Datadog } from '../tracking/datadog'
import UserSession from './UserSession'
import { getAuthCookieOptions } from './cookieOptions'
import { RemixBrowserCookie } from './remixBrowserCookie'

const ACCESS_TOKEN_KEY = 'fd_access_token'

export const AccessToken = {
  get() {
    return RemixBrowserCookie.get<string>(ACCESS_TOKEN_KEY)
  },

  mustGet() {
    const token = this.get()

    if (!token) {
      throw new Error('Token missing')
    }

    return token
  },

  set(token: string) {
    RemixBrowserCookie.set(ACCESS_TOKEN_KEY, token, getAuthCookieOptions())
  },

  clear() {
    RemixBrowserCookie.set(ACCESS_TOKEN_KEY, '', {
      ...getAuthCookieOptions(),
      expires: new Date(0)
    })
  },

  verify():
    | { token: string; valid: true }
    | { token?: undefined; valid: false } {
    const token = this.get()

    if (token) {
      const currentDateTime = new Date()
      const expiryDateTime = new Date(
        jwtDecode<{ exp: number }>(token).exp * 1000
      )
      const valid = expiryDateTime > currentDateTime

      return valid ? { token, valid } : { valid: false }
    } else {
      return { valid: false }
    }
  },

  requestHeaders() {
    const { token, valid } = this.verify()

    if (valid) {
      return this.requestHeadersWithToken(token)
    } else {
      return {}
    }
  },

  requestHeadersWithToken(maybeToken?: string) {
    const token = maybeToken ?? this.mustGet()
    return { authorization: `Bearer ${token}` }
  }
}

/**
 * Extract the email claim from an access token JWT, falling back to
 * Auth0-style namespaced custom claims (e.g. `https://focaldata.com/email`).
 *
 * Used as a fallback when the `fd_email` cookie isn't present — for example
 * when a user lands on cin-fd-surveys with only an `fd_access_token` shared
 * from a sibling app on the parent domain. Returns `null` when the token
 * is malformed or no email claim can be found.
 */
export const getEmailFromToken = (token: string): string | null => {
  try {
    const claims = jwtDecode<Record<string, unknown>>(token)
    if (typeof claims.email === 'string') {
      return claims.email
    }
    for (const [key, value] of Object.entries(claims)) {
      if (key.endsWith('/email') && typeof value === 'string') {
        return value
      }
    }
    return null
  } catch {
    return null
  }
}

export const setAccountId: (
  email: string,
  accessToken: string
) => Promise<boolean> = async (email, accessToken) => {
  const responseAccount = await GetAccountsByEmail(accessToken, email)
  const accounts: Account[] = responseAccount.data.slice()
  const { accountId, name: accountName } = accounts[0]
  if (accountId) {
    UserSession.setAccountId(accountId)
    UserSession.setAccountName(accountName)
    Datadog.updateUserSession({
      id: email,
      email,
      accountId,
      accountName
    })
    return true
  }

  return false
}

export const getLoginRedirectPath = (): string => {
  const { pathname } = window.location
  const targetPath = pathname === '/login' ? '/projects' : pathname
  return `/login?target=${targetPath}`
}

// Lowercased like the allowlist branch below: the stored address is whatever the
// server echoed, and staff detection gates the whole multi-market flow.
export const isFocaldataUser = (): boolean => {
  const email = (UserSession.getEmail() ?? '').toLowerCase()
  return email.endsWith('@focaldata.com')
}

const FEATURE_ACCESS_CONFIG = {
  MatrixMobileViewOnly: [
    'Josephine.Lichteblau@fgsglobal.com',
    'TMiller@forbes-tate.com',
    'DUsher@forbes-tate.com',
    'felix.schramm@fgsglobal.com',
    'raronoff-i@forbes-tate.com',
    'grainne@dataprax.is',
    'ben@dataprax.is',
    'phoebe@dataprax.is',
    'alex@dataprax.is',
    'pablo@dataprax.is',
    'manisha.juneja@btcorp.ae',
    'matthew@thehanwellgroup.com'
  ],
  TextHighlighter: [
    '@bursonglobal.com',
    '@hillandknowlton.com',
    'melanie.velarde@fgsglobal.com',
    'daria.sitokhina@fgsglobal.com',
    'tmiller@forbes-tate.com',
    'matthew@thehanwellgroup.com'
  ],
  Conjoint: [
    '@focaldata.com',
    '@stoneheaven.com',
    '@publicfirst.com',
    '@kcl.ac.uk',
    'matthew@thehanwellgroup.com'
  ],
  MaxDiff: ['matthew@thehanwellgroup.com'],
  HardDisqualification: [
    'manisha.juneja@btcorp.ae',
    'matthew@thehanwellgroup.com'
  ],
  FdChat: [
    'saml@cygn.al',
    'will@cygn.al',
    'dylank@cygn.al',
    'ehreno@cygn.al',
    'jaimes@cygn.al',
    'alexaa@cygn.al',
    'brianb@cygn.al',
    'nicholasv@cygn.al'
  ],
  // Internal-only for now — add client domains/emails here to grant early access.
  MultiLanguage: [],
  MultiMarket: [],
  // Global custom audience questions (multi-market). Own flag so it can be
  // dark-launched / killed independently of the broad MultiMarket grant.
  GlobalAudience: [],
  // The two halves of screener logic ship independently of each other.
  ScreeningRules: [],
  ScreenerDisplayLogic: [],
  // Multi-wave (tracker) projects.
  Tracker: []
} as const

export type FeatureName = keyof typeof FEATURE_ACCESS_CONFIG

export const hasFeatureAccess = (featureName: FeatureName): boolean => {
  if (isFocaldataUser()) {
    return true
  }

  const email = (UserSession.getEmail() ?? '').toLowerCase()
  const allowedDomains = FEATURE_ACCESS_CONFIG[featureName]
  return allowedDomains.some((domain) => email.endsWith(domain.toLowerCase()))
}

export const isDemoUser = (): boolean => {
  const email = UserSession.getEmail() ?? ''
  return (
    isFocaldataUser() &&
    (email.startsWith('demo+fdchat') || email.startsWith('fdchat+demo'))
  )
}

/**
 * Checks if the provided email matches the email associated with the current user session.
 * @param email The email to check against the current user's email.
 * @returns `true` if the provided email matches the current user's email, `false` otherwise.
 */
export const isUserWithEmail = (email: string): boolean => {
  return email.toLowerCase() === UserSession.getEmail()?.toLowerCase()
}

const baseCoworkerHostSchema = z
  .string()
  .url()
  .transform((str) => str.replace(/\/$/, ''))
const coworkerHostSchema =
  process.env.NODE_ENV === 'development'
    ? baseCoworkerHostSchema.default('http://localhost:3000')
    : baseCoworkerHostSchema
export const projectSetupHost = coworkerHostSchema.parse(
  // Could be an empty string, which we want to treat as undefined
  process.env.REACT_APP_PROJECT_SETUP_HOST || undefined
)
export const coworkerHost = coworkerHostSchema.parse(
  // Could be an empty string, which we want to treat as undefined
  process.env.REACT_APP_COWORKER_HOST || undefined
)
export const orchestraHost = coworkerHostSchema.parse(
  // Could be an empty string, which we want to treat as undefined
  process.env.REACT_APP_ORCHESTRA_HOST || undefined
)

export const appTargetSchema = z.enum([
  'project_setup',
  'audience_picker',
  'dashboard',
  'coworker',
  'orchestra'
])

export const targetDefaults: Record<z.infer<typeof appTargetSchema>, string> = {
  project_setup: '/',
  audience_picker: '/audience-picker',
  dashboard: '/projects',
  coworker: '/',
  orchestra: '/'
}

export interface LoginLocationState {
  firstLogin?: boolean
}
